import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import HugeiconsGift from '~icons/hugeicons/gift'
import MdiChevronRight from '~icons/mdi/chevron-right'
import { openSignModal } from 'src/components/Account/utils'
import { myTasksAtom } from 'src/components/GenerationTask/MultiColumnGenerationForm/context'
import {
  isPaymentAvailable,
  openMembershipPaymentUnavailableNotice,
} from 'src/components/Membership/MembershipPaymentUnavailableNotice'
import { UIDialog } from 'src/components/ui/Modal'
import Text from 'src/components/ui/Text'
import {
  type ExtraPackage,
  useExtraPackages,
  usePurchaseExtraPackage,
} from 'src/features/ExtraPackage'
import { OneTimePackCardV3 } from 'src/features/ExtraPackage/components/OneTimePackCardV3'
import { getOneTimeCardDisplayData } from 'src/features/ExtraPackage/domain/oneTimeCardData'
import { getNewbiePackOfferSummary } from 'src/features/ExtraPackage/domain/packCardMeta'
import UseGrowthSlot, { GrowthSlotName } from 'src/features/Growth/growth-slot'
import { useMyInfo } from 'src/services/apollo/auth'
import { refetchGetAllPaymentItems } from 'src/services/apollo/payment'
import { useMyQuota, useListMyQuotaLogs } from 'src/services/apollo/quota'
import { getLocale } from 'src/stores/intl'
import { formatCurrency, useCurrency } from 'src/utils/currency'
import { cn } from 'src/utils/cx'
import { getIsMobile } from 'src/utils/hooks/useBreakpoint'
import { useMatches } from 'src/utils/router/route'
import { track } from 'src/utils/tracker'
import { selectUANewbiePacks } from './selectUANewbiePacks'
import {
  getUANewbiePackTierByItemId,
  hasUANewbiePackPurchasableTier,
  isUANewbiePackEntryVisible,
  markUANewbiePackAhaShown,
  UANewbiePackEntryPoint,
  useUANewbiePackSnapshot,
} from './useUANewbiePack'

function useShouldShowEntryPoint(entryPoint: UANewbiePackEntryPoint) {
  const { data: snapshot } = useUANewbiePackSnapshot()

  return useMemo(() => {
    return (
      hasUANewbiePackPurchasableTier(snapshot) &&
      isUANewbiePackEntryVisible(snapshot, entryPoint)
    )
  }, [entryPoint, snapshot])
}

export function UANewbiePackDailyClaimSlot() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: snapshot } = useUANewbiePackSnapshot()
  const [currency] = useCurrency()
  const extraPackages = useExtraPackages({
    currentTier: 0,
    promotionOption: {},
  })
  const visible = useShouldShowEntryPoint(UANewbiePackEntryPoint.DailyClaim)
  const exposedRef = useRef(false)
  const offerSummary = useMemo(() => {
    return getNewbiePackOfferSummary({
      tiers: snapshot?.tiers,
      packs: extraPackages,
      currency,
    })
  }, [currency, extraPackages, snapshot?.tiers])
  const subtitleParts = [
    offerSummary.priceText
      ? t('growth:newbie-pack.banner.from-price', {
          price: offerSummary.priceText,
          defaultValue: 'From {{price}}',
        })
      : null,
    offerSummary.multiplierText
      ? t('growth:newbie-pack.banner.save-text', {
          multiplier: offerSummary.multiplierText,
          defaultValue: '{{multiplier}}x More Value',
        })
      : null,
  ].filter(Boolean)
  const subtitle = subtitleParts.join(' · ')

  useEffect(() => {
    if (!visible || exposedRef.current) return
    exposedRef.current = true
    track('newbie_pack_exposed', {
      entry_point: 'daily_claim',
      hours_since_trigger: snapshot?.hoursSinceTrigger ?? undefined,
    })
  }, [snapshot?.hoursSinceTrigger, visible])

  return (
    <UseGrowthSlot name={GrowthSlotName.DailyClaimBottom}>
      {visible ? (
        <div className="w-full rounded-xl p-[2px] bg-gradient-purple-pink">
          <button
            className={cn(
              'w-full h-14 rounded-[10px]',
              'bg-white dark:bg-neutral-900',
              'hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors',
              'px-3 flex items-center gap-3 text-left',
            )}
            onClick={() => {
              navigate('/membership/credit-packs')
            }}
          >
            <span className="size-9 rounded-lg bg-dspurple-500 text-white grid place-items-center shrink-0">
              <HugeiconsGift className="size-5" />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block font-semibold text-base leading-tight text-neutral-900 dark:text-neutral-100">
                <Text
                  i18nKey="growth:newbie-pack.title"
                  defaults="New creator gift!"
                />
              </span>
              <span className="block text-sm text-neutral-500 dark:text-neutral-400 leading-tight truncate">
                {subtitle}
              </span>
            </span>
            <MdiChevronRight className="size-5 text-neutral-400 dark:text-neutral-500 shrink-0" />
          </button>
        </div>
      ) : null}
    </UseGrowthSlot>
  )
}

export function UANewbiePackCreditPacksBanner(props: StyleProps) {
  const navigate = useNavigate()
  const { data: snapshot } = useUANewbiePackSnapshot()
  const visible = useShouldShowEntryPoint(UANewbiePackEntryPoint.CreditsPage)
  const exposedRef = useRef(false)

  useEffect(() => {
    if (!visible || exposedRef.current) return
    exposedRef.current = true
    track('newbie_pack_exposed', {
      entry_point: 'credits_page',
      hours_since_trigger: snapshot?.hoursSinceTrigger ?? undefined,
    })
  }, [snapshot?.hoursSinceTrigger, visible])

  if (!visible) return null

  return (
    <section
      className={cn(
        'border border-dspurple-300/50 rounded-xl',
        'bg-gradient-to-r from-fuchsia-100/70 via-white to-purple-100/80',
        'px-4 py-3 sm:px-6 sm:py-4',
        'flex items-center justify-between gap-3',
        props.className,
      )}
      style={props.style}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="size-10 rounded-xl bg-dspurple-500 text-white grid place-items-center shrink-0">
          <HugeiconsGift className="size-6" />
        </span>
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-bold leading-tight">
            <Text
              i18nKey="growth:newbie-pack.title"
              defaults="New creator gift"
            />
          </h2>
          <p className="text-sm text-neutral-500 truncate">
            <Text
              i18nKey="growth:newbie-pack.subtitle"
              defaults="Starter packs with limited-time newbie pricing"
            />
          </p>
        </div>
      </div>

      <button
        className="h-10 px-4 rounded-lg bg-dspurple-500 hover:bg-dspurple-500/90 text-white font-semibold shrink-0"
        onClick={() => {
          navigate('/membership/credit-packs')
        }}
      >
        <Text i18nKey="growth:newbie-pack.cta" defaults="View" />
      </button>
    </section>
  )
}

function UANewbiePackFloatingDialogContent(props: {
  close: () => void
  source?: string
}) {
  const navigate = useNavigate()

  return (
    <div className="w-[min(92vw,460px)] p-5 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="size-10 rounded-xl bg-dspurple-500 text-white grid place-items-center shrink-0">
          <HugeiconsGift className="size-6" />
        </span>
        <h2 className="text-xl font-bold">
          <Text
            i18nKey="growth:newbie-pack.title"
            defaults="New creator gift"
          />
        </h2>
      </div>
      <p className="text-sm text-neutral-500">
        <Text
          i18nKey="growth:newbie-pack.floating.subtitle"
          defaults="Starter packs are unlocked after your first generation."
        />
      </p>
      <button
        className="h-11 rounded-lg bg-dspurple-500 hover:bg-dspurple-500/90 text-white font-semibold"
        onClick={() => {
          props.close()
          navigate('/membership/credit-packs')
        }}
      >
        <Text i18nKey="growth:newbie-pack.cta" defaults="View offers" />
      </button>
    </div>
  )
}

export function openUANewbiePackFloatingDialog(args?: { source?: string }) {
  UIDialog.create({
    dismissable: true,
    type: getIsMobile() ? 'drawer' : 'dialog',
    className: 'min-w-[unset] max-w-[unset]',
    content: ({ close }) => (
      <UANewbiePackFloatingDialogContent close={close} source={args?.source} />
    ),
  })
}

export function UANewbiePackFloatingPopup() {
  const tasks = useAtomValue(myTasksAtom)
  const { data: me } = useMyInfo()
  const { data: snapshot, refetch: refetchSnapshot } = useUANewbiePackSnapshot()
  const [completedGenerationTick, setCompletedGenerationTick] = useState(0)
  const completedTaskIdsRef = useRef<Set<string> | null>(null)
  const optimisticExposeRunningRef = useRef(false)

  useEffect(() => {
    const completedTaskIds = new Set(
      tasks.filter(task => task.status === 'completed').map(task => task.id),
    )
    if (!completedTaskIdsRef.current) {
      completedTaskIdsRef.current = completedTaskIds
      return
    }

    const hasNewCompletedTask = [...completedTaskIds].some(
      id => !completedTaskIdsRef.current?.has(id),
    )
    completedTaskIdsRef.current = completedTaskIds
    if (hasNewCompletedTask) {
      setCompletedGenerationTick(tick => tick + 1)
    }
  }, [tasks])

  useEffect(() => {
    if (!me?.id) return
    if (!hasUANewbiePackPurchasableTier(snapshot)) return
    if (snapshot?.ahaShownAt) return
    if (!isUANewbiePackEntryVisible(snapshot, UANewbiePackEntryPoint.Floating))
      return

    const key = `ua-newbie-pack-floating-shown:${me.id}`
    if (sessionStorage.getItem(key) === '1') return

    sessionStorage.setItem(key, '1')
    track('newbie_pack_exposed', {
      entry_point: 'floating',
      source: 'generator',
      hours_since_trigger: snapshot?.hoursSinceTrigger ?? undefined,
    })
    openUANewbiePackAhaPurchaseDialog({ source: 'generator-floating-auto' })
    void markUANewbiePackAhaShown().catch(() => {
      // Do not block popup display if the shown marker cannot be persisted.
    })
  }, [me?.id, snapshot])

  useEffect(() => {
    if (!me?.id) return
    if (!completedGenerationTick) return
    if (optimisticExposeRunningRef.current) return
    if (snapshot?.ahaShownAt) return

    const key = `ua-newbie-pack-floating-shown:${me.id}`
    if (sessionStorage.getItem(key) === '1') return

    let cancelled = false
    optimisticExposeRunningRef.current = true

    void (async () => {
      try {
        const { data: freshSnapshot } = await refetchSnapshot()
        if (cancelled) return
        const popupSnapshot = freshSnapshot ?? snapshot
        if (
          !hasUANewbiePackPurchasableTier(popupSnapshot) ||
          !isUANewbiePackEntryVisible(
            popupSnapshot,
            UANewbiePackEntryPoint.Floating,
          )
        ) {
          return
        }

        sessionStorage.setItem(key, '1')
        track('newbie_pack_exposed', {
          entry_point: 'floating',
          source: 'generator-completion',
          hours_since_trigger: popupSnapshot?.hoursSinceTrigger ?? undefined,
        })
        openUANewbiePackAhaPurchaseDialog({
          source: 'generator-completion',
        })
        void markUANewbiePackAhaShown().catch(() => {
          // Do not block popup display if the shown marker cannot be persisted.
        })
      } catch {
        // Do not block generation flow on promotion API failures.
      } finally {
        optimisticExposeRunningRef.current = false
      }
    })()

    return () => {
      cancelled = true
      optimisticExposeRunningRef.current = false
    }
  }, [completedGenerationTick, me?.id, refetchSnapshot, snapshot])

  return null
}

function UANewbiePackAhaPurchaseDialogContent(props: {
  close: () => void
  source?: string
}) {
  const { t } = useTranslation(['membership'])
  const locale = getLocale()
  const { data: userInfo } = useMyInfo()
  const { data: snapshot } = useUANewbiePackSnapshot()
  const [currency] = useCurrency()
  const extraPackages = useExtraPackages({
    currentTier: 0,
    promotionOption: {},
  })
  const purchase = usePurchaseExtraPackage({ promotionOption: {} })
  const matches = useMatches()
  const scene = (matches.at(-1)?.handle as { name?: string })?.name
  const { refetch: refreshCreditHistory } = useListMyQuotaLogs({ last: 20 })
  const { refetch: refreshCreditBalance } = useMyQuota()
  const [buyingId, setBuyingId] = useState<string | null>(null)

  const oneTimePacks = useMemo(
    () => selectUANewbiePacks(extraPackages, snapshot),
    [extraPackages, snapshot],
  )

  const buyPack = async (pack: ExtraPackage) => {
    if (!userInfo) return openSignModal(undefined, { fromPath: 'membership' })
    if (!isPaymentAvailable()) {
      return openMembershipPaymentUnavailableNotice({ type: 'unavailable' })
    }
    if (buyingId) return

    setBuyingId(pack.id)
    const tier = getUANewbiePackTierByItemId(
      snapshot,
      pack.id,
      pack.legacyItemId,
    )
    if (tier) {
      track('newbie_pack_clicked', {
        entry_point: 'floating',
        target_tier: tier.tierCode,
      })
    }
    const result = await purchase({
      extraPackageId: pack.id,
      prices: pack.prices,
      scene,
    })
    setBuyingId(null)
    if (!result) return

    refreshCreditBalance()
    refreshCreditHistory()
    refetchGetAllPaymentItems()
    props.close()
  }

  return (
    <div className="w-full h-full box-border overflow-x-hidden p-4 sm:p-6">
      <div className="flex flex-col items-center text-center">
        <span className="size-14 rounded-2xl bg-gradient-purple-pink-portrait text-white grid place-items-center shrink-0 shadow-sm">
          <HugeiconsGift className="size-8" />
        </span>
        <h2 className="mt-4 text-lg sm:text-xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">
          <Text i18nKey="growth:newbie-pack.title" defaults="Welcome Gift" />
        </h2>
        <p className="mt-1.5 text-sm sm:text-xl font-medium text-neutral-500 dark:text-neutral-400">
          <Text
            i18nKey="growth:newbie-pack.aha.subtitle"
            defaults="Don't miss this one-time deal — start creating without limits!"
          />
        </p>
      </div>

      <div className="my-6 h-px bg-neutral-200 dark:bg-neutral-700" />
      <p className="text-left text-xs sm:text-sm font-bold tracking-wide text-neutral-700 dark:text-neutral-200 uppercase mb-8">
        <Text
          i18nKey="growth:newbie-pack.aha.choose-pack"
          defaults="Choose your pack"
        />
      </p>

      <div className="grid w-full mx-auto gap-3 sm:gap-4 grid-cols-2">
        {oneTimePacks.map((pack, index) => {
          const tierValue = pack.values[0]
          const credits = tierValue?.creditsAfterBoosts ?? tierValue?.value ?? 0
          const cardData = getOneTimeCardDisplayData({
            marketingMetadata: pack.marketingMetadata,
            packageContents: pack.packageContents ?? [],
            t,
          })
          return (
            <OneTimePackCardV3
              key={pack.id}
              title={cardData.title}
              rows={cardData.rows}
              decorations={cardData.decorations}
              multiplier={cardData.multiplier}
              boostedCredits={credits}
              priceLabel={formatCurrency(pack.prices[currency], { currency })}
              locale={locale}
              animateCredits={false}
              equivalentImagesText={cardData.equivalentImagesText}
              featured={
                index === oneTimePacks.length - 1 && oneTimePacks.length > 1
              }
              hasKaisuuken={cardData.hasKaisuuken}
              busy={buyingId === pack.id}
              disabled={buyingId !== null && buyingId !== pack.id}
              showSuccess={false}
              onPurchase={() => buyPack(pack)}
            />
          )
        })}
      </div>

      <p className="mt-5 text-xs sm:text-sm font-medium text-neutral-400 dark:text-neutral-500 text-center">
        <Text
          i18nKey="growth:newbie-pack.aha.note"
          defaults="* Each pack can only be purchased once"
        />
      </p>
    </div>
  )
}

export function openUANewbiePackAhaPurchaseDialog(args?: { source?: string }) {
  UIDialog.create({
    dismissable: true,
    type: getIsMobile() ? 'drawer' : 'dialog',
    className: 'min-w-[unset] max-w-[unset]',
    content: ({ close }) => (
      <UANewbiePackAhaPurchaseDialogContent
        close={close}
        source={args?.source}
      />
    ),
  })
}
